import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import DatePicker from 'react-datepicker'
import { connect } from 'react-redux'
import { fromZonedTime } from 'date-fns-tz'
import subDays from 'date-fns/subDays'
import startOfDay from 'date-fns/startOfDay'
import endOfDay from 'date-fns/endOfDay'
import { useTranslation } from 'react-i18next'

import { getValue, getAnalyticsConfig } from '../helpers'
import {
  convertMinutesToDaysHoursMinutes,
  timeIntervals,
  generateRange,
} from '../../../../helpers/helpers'
import API from '../../../../api/api'

const MaintenanceRequestsDataReports = ({ buildingId }) => {
  const { t } = useTranslation(['maintenance'])
  const [errorMessage, setErrorMessage] = useState(null)
  const [isLoading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState(() => {
    return [subDays(startOfDay(Date.now()), 30), startOfDay(Date.now())]
  })
  const [startDate, endDate] = dateRange
  const [reports, setReports] = useState([])
  const [showPicker, setShowPicker] = useState(false)

  const getAnalytics = (values) => {
    const dataForAnalytics = Object.keys(values)

    return getAnalyticsConfig(t).map((analytic, i) => ({
      ...analytic,
      value: values[dataForAnalytics[i]],
    }))
  }

  useEffect(() => {
    ;(async () => {
      if (buildingId) {
        try {
          setLoading(true)
          const [startDate, endDate] = dateRange
          if (endDate) {
            const formattedStartDate = startDate && fromZonedTime(startDate, 'UTC').getTime()
            const formattedEndDate = endDate && fromZonedTime(endOfDay(endDate), 'UTC').getTime()

            const { data } = await API.handleFetchAnalyticsForMaintenanceRequests(
              formattedStartDate,
              formattedEndDate,
              buildingId,
            )

            setReports(getAnalytics(data))
          }
        } catch (error) {
          setErrorMessage(error.message)
        } finally {
          setLoading(false)
        }
      }
    })()
  }, [dateRange, buildingId])

  const setDates = (value) => {
    setShowPicker(false)
    setDateRange(generateRange(value))
  }

  return (
    <div className="data-reports">
      <div className={'data-reports__header'}>
        <span className="data-reports__header__title">
          {t('maintenance:requests.dataReports.title')}
        </span>
        <DatePicker
          className={'input'}
          selectsRange={true}
          startDate={startDate}
          endDate={endDate}
          placeholderText={t('maintenance:requests.dateRangePlaceholder')}
          isClearable={true}
          onClickOutside={(e) => {
            if (timeIntervals.includes(e.target.value)) {
              setDates(e.target.value)
            }
            setShowPicker(false)
          }}
          onCalendarOpen={() => setShowPicker(true)}
          value={getValue(...dateRange)}
          onChange={(update) => {
            setDateRange(update)
            update[1] && setShowPicker(false)
          }}
        />
        <div
          style={{ display: showPicker ? 'block' : 'none' }}
          className="interval-picker-container"
        >
          <div className="interval-picker">
            <div className="period-title">{t('maintenance:requests.dataReports.period')}</div>
            <div className="intervals-scroll">
              {timeIntervals.map((interval, i) => (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                  key={interval + i}
                  value={interval}
                  className="interval"
                >
                  {interval}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="ctp-building-navbar__error-message">{errorMessage}</div>
      <div className="data-reports__list">
        {!isLoading && reports.length
          ? reports.map((report, i) => (
              <div className="data-reports__list__report" key={i}>
                <div className="data-reports__list__report__header">
                  <div className="data-reports__list__report__time-text">{report.timeText}</div>
                  <div className={`data-reports__list__report__type ${report.type}`}>
                    {report.type}
                  </div>
                </div>
                <p className="data-reports__list__report__time">
                  {!report.value
                    ? t('maintenance:requests.dataReports.notAvailable')
                    : report.unit === 'time'
                      ? convertMinutesToDaysHoursMinutes(report.value)
                      : report.value}
                </p>
                <p className="data-reports__list__report__description">{report.description}</p>
              </div>
            ))
          : !isLoading &&
            !reports.length && <p>{t('maintenance:requests.dataReports.noAnalyticsYet')}</p>}
      </div>
    </div>
  )
}

MaintenanceRequestsDataReports.propTypes = {
  buildingId: PropTypes.string,
}

export default connect(() => ({}), {})(MaintenanceRequestsDataReports)
